import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || 'https://fctvfeimksahulfiskdp.supabase.co';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

const supabase = createClient(supabaseUrl, supabaseAnonKey);

const EDGE_FUNCTION_URL = process.env.REACT_APP_EDGE_FUNCTION_URL || supabaseUrl;

const EDGE_HEADERS = {
  'Content-Type': 'application/json',
  'apikey': supabaseAnonKey,
  'Authorization': `Bearer ${supabaseAnonKey}`,
};

export { scheduleConsultation, submitJobApplication, submitContactForm, subscribeNewsletter, getJobOpenings, getContactForms, getJobApplications, getNewsletterSubscribers, getConsultations, getStatusChecks, adminLogin, createAdmin, updateAdmin, deleteAdmin, getAdmins, createJobOpening, updateJobOpening, deleteJobOpening, seedJobOpenings };

async function scheduleConsultation(data) {
  const response = await fetch(`${EDGE_FUNCTION_URL}/functions/v1/consultations`, {
    method: 'POST',
    headers: EDGE_HEADERS,
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to schedule consultation');
  }
  return response.json();
}

async function submitJobApplication(formData) {
  const response = await fetch(`${EDGE_FUNCTION_URL}/functions/v1/job-applications`, {
    method: 'POST',
    headers: EDGE_HEADERS,
    body: JSON.stringify(formData),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to submit application');
  }
  return response.json();
}

async function submitContactForm(data) {
  const response = await fetch(`${EDGE_FUNCTION_URL}/functions/v1/contact-forms`, {
    method: 'POST',
    headers: EDGE_HEADERS,
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to submit contact form');
  }
  return response.json();
}

async function subscribeNewsletter(data) {
  const response = await fetch(`${EDGE_FUNCTION_URL}/functions/v1/newsletter`, {
    method: 'POST',
    headers: EDGE_HEADERS,
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to subscribe');
  }
  return response.json();
}

async function getJobOpenings() {
  const response = await fetch(`${EDGE_FUNCTION_URL}/functions/v1/job-openings`, {
    headers: EDGE_HEADERS,
  });
  if (!response.ok) throw new Error('Failed to fetch job openings');
  return response.json();
}

async function getContactForms(adminEmail) {
  const response = await fetch(`${EDGE_FUNCTION_URL}/functions/v1/contact-forms`, {
    headers: adminEmail ? { ...EDGE_HEADERS, 'x-admin-email': adminEmail } : EDGE_HEADERS,
  });
  if (!response.ok) throw new Error('Failed to fetch contact forms');
  return response.json();
}

async function getJobApplications(adminEmail) {
  const response = await fetch(`${EDGE_FUNCTION_URL}/functions/v1/job-applications`, {
    headers: adminEmail ? { ...EDGE_HEADERS, 'x-admin-email': adminEmail } : EDGE_HEADERS,
  });
  if (!response.ok) throw new Error('Failed to fetch job applications');
  return response.json();
}

async function getNewsletterSubscribers(adminEmail) {
  const response = await fetch(`${EDGE_FUNCTION_URL}/functions/v1/newsletter`, {
    headers: adminEmail ? { ...EDGE_HEADERS, 'x-admin-email': adminEmail } : EDGE_HEADERS,
  });
  if (!response.ok) throw new Error('Failed to fetch subscribers');
  return response.json();
}

async function getConsultations(adminEmail) {
  const { data, error } = await supabase
    .from('consultations')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data;
}

async function getStatusChecks() {
  const response = await fetch(`${EDGE_FUNCTION_URL}/functions/v1/status-checks`, {
    headers: EDGE_HEADERS,
  });
  if (!response.ok) throw new Error('Failed to fetch status checks');
  return response.json();
}

async function adminLogin(email, password) {
  const response = await fetch(`${EDGE_FUNCTION_URL}/functions/v1/admin-auth`, {
    method: 'POST',
    headers: EDGE_HEADERS,
    body: JSON.stringify({ email, password }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Invalid email or password');
  }
  return response.json();
}

async function createAdmin(email, password, role, adminEmail) {
  const response = await fetch(`${EDGE_FUNCTION_URL}/functions/v1/admin-auth`, {
    method: 'POST',
    headers: {
      ...EDGE_HEADERS,
      'x-admin-email': adminEmail,
    },
    body: JSON.stringify({ email, password, role }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create admin');
  }
  return response.json();
}

async function updateAdmin(targetEmail, data, adminEmail) {
  const response = await fetch(`${EDGE_FUNCTION_URL}/functions/v1/admin-auth/${encodeURIComponent(targetEmail)}`, {
    method: 'PUT',
    headers: {
      ...EDGE_HEADERS,
      'x-admin-email': adminEmail,
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update admin');
  }
  return response.json();
}

async function deleteAdmin(targetEmail, adminEmail) {
  const response = await fetch(`${EDGE_FUNCTION_URL}/functions/v1/admin-auth/${encodeURIComponent(targetEmail)}`, {
    method: 'DELETE',
    headers: {
      ...EDGE_HEADERS,
      'x-admin-email': adminEmail,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete admin');
  }
  return response.json();
}

async function getAdmins(adminEmail) {
  const response = await fetch(`${EDGE_FUNCTION_URL}/functions/v1/admin-auth`, {
    headers: {
      ...EDGE_HEADERS,
      'x-admin-email': adminEmail,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch admins');
  }
  return response.json();
}

async function createJobOpening(data) {
  const response = await fetch(`${EDGE_FUNCTION_URL}/functions/v1/job-openings`, {
    method: 'POST',
    headers: EDGE_HEADERS,
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create opening');
  }
  return response.json();
}

async function updateJobOpening(id, data) {
  const response = await fetch(`${EDGE_FUNCTION_URL}/functions/v1/job-openings/${id}`, {
    method: 'PUT',
    headers: EDGE_HEADERS,
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update opening');
  }
  return response.json();
}

async function deleteJobOpening(id) {
  const response = await fetch(`${EDGE_FUNCTION_URL}/functions/v1/job-openings/${id}`, {
    method: 'DELETE',
    headers: EDGE_HEADERS,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete opening');
  }
  return response.json();
}

async function seedJobOpenings(openings) {
  const response = await fetch(`${EDGE_FUNCTION_URL}/functions/v1/job-openings/seed`, {
    method: 'POST',
    headers: EDGE_HEADERS,
    body: JSON.stringify(openings),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to seed openings');
  }
  return response.json();
}

export { supabase };
